import type { FC, ReactNode } from 'react'

import { ArrowRight, Headset, LifeBuoy, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { LiveDot } from '@/components/generals/live-dot'

const SERVICES = [
    { name: "Carrier feeds", status: "Operational" },
    { name: "Customs gateways", status: "Operational" },
    { name: "Open API", status: "Operational" },
    { name: "Flow Studio", status: "Operational" },
    { name: "Pilot", status: "Operational" },
    { name: "Ops desk", status: "Staffed 24/7" }
]

const UPTIME = Array.from({ length: 30 }, (_, index) => index === 17 ? 'minor' : 'ok')

interface ContactCardsProps {
    onOpenSupport: () => void
}

/** Speak to a human, talk to support, and live system status. */
export const ContactCards: FC<ContactCardsProps> = ({ onOpenSupport }): ReactNode => {
    return (
        <div className='grid gap-4 lg:grid-cols-3'>
            <Reveal id='speak-to-a-human' className='surface flex flex-col rounded-[28px] p-6 sm:p-7'>
                <span className='grid size-11 place-items-center rounded-xl bg-cream text-ink'>
                    <Headset aria-hidden='true' className='size-5' strokeWidth={1.6} />
                </span>

                <h2 className='mt-6 font-heading text-xl font-semibold tracking-[-0.025em] text-ink'>
                    Speak to a human
                </h2>

                <p className='mt-2 text-[15px] leading-relaxed text-ink-soft'>
                    Call the solutions team directly. Live customers reach the ops desk on the same numbers, day or night.
                </p>

                <ul className='mt-6 space-y-3'>
                    <li>
                        <a
                            href='tel:+442079460123'
                            className='group/phone flex items-center justify-between gap-3 rounded-2xl bg-cream px-4 py-3 transition-colors hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                        >
                            <span>
                                <span className='eyebrow block text-stone'>
                                    Europe
                                </span>

                                <span className='mt-0.5 block font-medium text-ink'>
                                    +44 20 7946 0123
                                </span>
                            </span>

                            <Phone aria-hidden='true' className='size-4 text-stone transition-colors group-hover/phone:text-ultramarine' />
                        </a>
                    </li>

                    <li>
                        <a
                            href='tel:+13125550148'
                            className='group/phone flex items-center justify-between gap-3 rounded-2xl bg-cream px-4 py-3 transition-colors hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                        >
                            <span>
                                <span className='eyebrow block text-stone'>
                                    Americas
                                </span>

                                <span className='mt-0.5 block font-medium text-ink'>
                                    +1 312 555 0148
                                </span>
                            </span>

                            <Phone aria-hidden='true' className='size-4 text-stone transition-colors group-hover/phone:text-ultramarine' />
                        </a>
                    </li>
                </ul>

                <p className='mt-auto pt-6 text-[13px] text-stone'>
                    Weekdays, 07:00 to 19:00 UK time and 08:00 to 18:00 Central time.
                </p>
            </Reveal>

            <Reveal id='support' delay={100} className='surface flex flex-col rounded-[28px] p-6 sm:p-7'>
                <span className='grid size-11 place-items-center rounded-xl bg-cream text-ink'>
                    <LifeBuoy aria-hidden='true' className='size-5' strokeWidth={1.6} />
                </span>

                <h2 className='mt-6 font-heading text-xl font-semibold tracking-[-0.025em] text-ink'>
                    Talk to support
                </h2>

                <p className='mt-2 text-[15px] leading-relaxed text-ink-soft'>
                    Already live with Quayside? Open a support request and the ops desk picks it up within 30 minutes, at any hour.
                </p>

                <dl className='mt-6 grid grid-cols-2 gap-3'>
                    <div className='rounded-2xl bg-cream px-3.5 py-3 sm:px-4'>
                        <dt className='eyebrow text-stone'>
                            First reply
                        </dt>

                        <dd className='mt-1 font-heading text-base font-semibold tracking-tight text-ink sm:text-lg'>
                            Under 30 min
                        </dd>
                    </div>

                    <div className='rounded-2xl bg-cream px-3.5 py-3 sm:px-4'>
                        <dt className='eyebrow text-stone'>
                            Coverage
                        </dt>

                        <dd className='mt-1 font-heading text-base font-semibold tracking-tight text-ink sm:text-lg'>
                            24/7/365
                        </dd>
                    </div>
                </dl>

                <button
                    type='button'
                    onClick={onOpenSupport}
                    className='group/support mt-auto inline-flex items-center gap-1.5 self-start pt-6 text-sm font-medium text-ink transition-colors hover:text-ultramarine focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                >
                    Open a support request
                    <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/support:translate-x-1' />
                </button>
            </Reveal>

            <Reveal id='system-status' delay={200} className='surface flex flex-col rounded-[28px] p-6 sm:p-7'>
                <div className='flex items-center justify-between gap-3'>
                    <h2 className='font-heading text-xl font-semibold tracking-[-0.025em] text-ink'>
                        System status
                    </h2>

                    <span className='eyebrow flex items-center gap-2 text-ink-soft'>
                        <LiveDot />
                        Live
                    </span>
                </div>

                <p className='mt-2 text-[15px] text-ink-soft'>
                    All systems running.
                </p>

                <ul className='mt-5 space-y-1.5'>
                    {SERVICES.map(service => (
                        <li key={service.name} className='flex items-center justify-between gap-3 rounded-xl bg-cream px-3.5 py-2.5 text-[14px]'>
                            <span className='text-ink'>
                                {service.name}
                            </span>

                            <span className='flex items-center gap-2 text-[#1b7f45]'>
                                <span aria-hidden='true' className='size-2 rounded-full bg-signal' />
                                {service.status}
                            </span>
                        </li>
                    ))}
                </ul>

                <div className='mt-5'>
                    <div className='flex items-end gap-[3px]' aria-hidden='true'>
                        {UPTIME.map((day, index) => (
                            <span
                                key={index}
                                className={cn('h-7 flex-1 rounded-[3px]', day === 'ok' ? 'bg-signal/70' : 'bg-[#f2b84b]')}
                            />
                        ))}
                    </div>

                    <p className='eyebrow mt-3 flex justify-between text-stone'>
                        <span>
                            30 days ago
                        </span>

                        <span>
                            99.98% uptime
                        </span>
                    </p>

                    <p className='sr-only'>
                        99.98% uptime over the last 30 days, with one minor incident.
                    </p>
                </div>
            </Reveal>
        </div>
    )
}
